import React, { useState, useEffect } from 'react';
import { ArrowRight, Clock, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from '../hooks/useTranslation';

const HeroSection = ({ onOrderClick }) => {
  const { t, language } = useTranslation();
  const [storeStatus, setStoreStatus] = useState({ isOpen: true, message: '', color: 'green' });
  
  useEffect(() => {
    const checkStoreHours = () => {
      // Get Riga time (GMT+2)
      const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Riga' }));
      const day = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const currentTime = hours + minutes / 60;
      
      // Monday is closed (day === 1)
      if (day === 1) {
        const translations = {
          lv: `Slēgts • Pirmdiena ir brīvdiena`,
          en: `Closed • Monday is a day off`,
          ru: `Закрыто • Понедельник выходной`
        };
        setStoreStatus({ 
          isOpen: false, 
          message: translations[language] || translations.lv, 
          color: 'red' 
        });
        return;
      }
      
      // Opening hours: Tue-Thu: 12-21, Fri-Sat: 12-22, Sun: 12-21
      let openTime = 12;
      let closeTime;
      
      if (day === 5 || day === 6) { // Friday or Saturday
        closeTime = 22;
      } else { // Sunday, Tuesday - Thursday
        closeTime = 21;
      }
      
      const isCurrentlyOpen = currentTime >= openTime && currentTime < closeTime;
      
      let message = '';
      let color = 'green';
      
      if (isCurrentlyOpen) {
        const closeHour = Math.floor(closeTime);
        const closeMin = Math.floor((closeTime % 1) * 60);
        const translations = {
          lv: `Atvērts • Slēdzas plkst. ${closeHour}:${closeMin.toString().padStart(2, '0')}`,
          en: `Open • Closes at ${closeHour}:${closeMin.toString().padStart(2, '0')}`,
          ru: `Открыто • Закрывается в ${closeHour}:${closeMin.toString().padStart(2, '0')}`
        };
        message = translations[language] || translations.lv;
        color = 'green';
      } else {
        // Store is closed - show next opening
        const tomorrow = new Date(now);
        if (currentTime >= closeTime) {
          tomorrow.setDate(tomorrow.getDate() + 1);
        }
        
        // Skip Monday if tomorrow is Monday
        if (tomorrow.getDay() === 1) {
          tomorrow.setDate(tomorrow.getDate() + 1); // Move to Tuesday
        }
        
        const translations = {
          lv: `Slēgts • Atvērsies plkst. 12:00`,
          en: `Closed • Opens at 12:00`,
          ru: `Закрыто • Откроется в 12:00`
        };
        message = translations[language] || translations.lv;
        color = 'red';
      }
      
      setStoreStatus({ isOpen: isCurrentlyOpen, message, color });
    };
    
    checkStoreHours();
    const interval = setInterval(checkStoreHours, 60000);
    
    return () => clearInterval(interval);
  }, [language]);
  
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_mikado-eats/artifacts/qwer46us_KOPBILDE.JPG"
          alt="Mikado Sushi"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-4xl">
          {/* Content */}
          <div className="text-white space-y-8">
            <div className="inline-block mb-8">
              <span className="inline-block px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm font-medium tracking-[0.2em] uppercase">
                {t('fresh')} • {t('authentic')} • {t('delivered')}
              </span>
            </div>
            
            {/* Store Status */}
            <div className="inline-block mb-4">
              <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
                storeStatus.color === 'green' 
                  ? 'bg-green-500/20 border border-green-500/30' 
                  : 'bg-red-500/20 border border-red-500/30'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  storeStatus.color === 'green' ? 'bg-green-500' : 'bg-red-500'
                } animate-pulse`}></div>
                <span className="text-white font-semibold text-xs sm:text-sm">{storeStatus.message}</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[0.95] mb-4 sm:mb-6 md:mb-8 tracking-tight">
              {t('heroTitle')}
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed max-w-2xl mb-6 sm:mb-8 md:mb-10 px-2 sm:px-0">
              {t('heroDescription')}
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-8 mb-6 sm:mb-10">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center border border-white/20">
                  <Clock className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-400 uppercase tracking-wider mb-0.5 sm:mb-1">{t('deliveryTime')}</div>
                  <div className="font-semibold text-sm sm:text-lg">{t('deliveryMins')}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center border border-white/20">
                  <MapPin className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-400 uppercase tracking-wider mb-0.5 sm:mb-1">{t('freeDelivery')}</div>
                  <div className="font-semibold text-sm sm:text-lg">{t('ordersOver')}</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 pt-4 sm:pt-6 px-2 sm:px-0">
              <Button
                onClick={onOrderClick}
                size="lg"
                className="bg-white text-black hover:bg-gray-100 font-semibold px-6 sm:px-10 py-4 sm:py-7 text-base sm:text-lg group transition-all duration-300 transform hover:scale-105 hover:shadow-2xl rounded-xl w-full sm:w-auto"
              >
                {t('orderNow')}
                <ArrowRight className="ml-2 sm:ml-3 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 hover:border-white font-semibold px-6 sm:px-10 py-4 sm:py-7 text-base sm:text-lg transition-all duration-300 rounded-xl backdrop-blur-sm w-full sm:w-auto"
              >
                {t('viewMenu')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - hidden on mobile */}
      <div className="hidden sm:block absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;